import React, { useEffect, useState } from "react";
import { backend_url } from "../../App";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../FabricForm/FabricForm.css"; // shared popup styling (fform-* classes)

const emptyForm = () => ({
  floralCollection: "", surfacePatternDesign: "", colourVariations: "", scaleVariations: "",
  moq: "", productionStatus: "Design", sampleApproved: false,
});

const PrintDesignForm = ({ designId, onClose, onSaved }) => {
  const isNew = !designId;
  const [form, setForm] = useState(emptyForm());
  const [images, setImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!isNew) fetchDesign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  const fetchDesign = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backend_url}/api/print-designs/${designId}`);
      const data = await res.json();
      if (!data.success) throw new Error();
      setRecord(data.design);
      setForm({
        floralCollection: data.design.floralCollection || "",
        surfacePatternDesign: data.design.surfacePatternDesign || "",
        colourVariations: (data.design.colourVariations || []).join(", "),
        scaleVariations: (data.design.scaleVariations || []).join(", "),
        moq: data.design.moq ?? "",
        productionStatus: data.design.productionStatus || "Design",
        sampleApproved: !!data.design.sampleApproved,
      });
      setImages(data.design.referenceImages || []);
    } catch {
      toast.error("Failed to load design");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (images.length + newFiles.length + files.length > 6) {
      toast.error("Max 6 images allowed");
      return;
    }
    setNewFiles((p) => [...p, ...files]);
  };
  const removeNewFile = (i) => setNewFiles((p) => p.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        newFiles.forEach((f) => fd.append("images", f));
        const res = await fetch(`${backend_url}/api/print-designs`, { method: "POST", body: fd });
        const data = await res.json();
        if (!data.success) throw new Error();
        toast.success("Design added");
        onSaved && onSaved();
        onClose();
      } else {
        const res = await fetch(`${backend_url}/api/print-designs/${designId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (!data.success) throw new Error();

        if (newFiles.length > 0) {
          const fd = new FormData();
          newFiles.forEach((f) => fd.append("images", f));
          const imgRes = await fetch(`${backend_url}/api/print-designs/${designId}/images`, { method: "POST", body: fd });
          const imgData = await imgRes.json();
          if (imgData.success) setImages(imgData.design.referenceImages);
          setNewFiles([]);
        }
        toast.success("Design updated");
        setRecord(data.design);
        onSaved && onSaved();
      }
    } catch {
      toast.error("Failed to save design");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fform-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="fform-modal">
          <button className="fform-close" onClick={onClose}>✕</button>
          <div className="fform-header">
            <h2>{isNew ? "Add Print Design" : form.floralCollection || "Print Design"}</h2>
            {record?.printDesignId && <span className="fform-id-chip">{record.printDesignId}</span>}
          </div>

          <div className="fform-body">
            {loading ? (
              <p className="fform-loading">Loading…</p>
            ) : (
              <>
                <div className="fform-images-section">
                  <span className="fform-images-label">Reference Images</span>
                  <div className="fform-images-row">
                    {images.map((url) => <div key={url} className="fform-img-thumb"><img src={url} alt="design" /></div>)}
                    {newFiles.map((file, i) => (
                      <div key={i} className="fform-img-thumb fform-img-pending">
                        <img src={URL.createObjectURL(file)} alt="pending" />
                        <button onClick={() => removeNewFile(i)}>✕</button>
                      </div>
                    ))}
                    {images.length + newFiles.length < 6 && (
                      <label className="fform-img-upload-zone">+ Add<input type="file" accept="image/*" multiple hidden onChange={handleFileSelect} /></label>
                    )}
                  </div>
                </div>

                <div className="fform-grid">
                  <label className="fform-field"><span>Floral Collection</span>
                    <input type="text" value={form.floralCollection} onChange={(e) => handleChange("floralCollection", e.target.value)} />
                  </label>
                  <label className="fform-field"><span>Surface Pattern Design</span>
                    <input type="text" value={form.surfacePatternDesign} onChange={(e) => handleChange("surfacePatternDesign", e.target.value)} />
                  </label>
                  <label className="fform-field"><span>Colour Variations (comma-separated)</span>
                    <input type="text" value={form.colourVariations} onChange={(e) => handleChange("colourVariations", e.target.value)} />
                  </label>
                  <label className="fform-field"><span>Scale Variations (comma-separated)</span>
                    <input type="text" value={form.scaleVariations} onChange={(e) => handleChange("scaleVariations", e.target.value)} />
                  </label>
                  <label className="fform-field"><span>MOQ</span>
                    <input type="number" value={form.moq} onChange={(e) => handleChange("moq", e.target.value)} />
                  </label>
                  <label className="fform-field"><span>Production Status</span>
                    <select value={form.productionStatus} onChange={(e) => handleChange("productionStatus", e.target.value)}>
                      {["Design", "Sample", "Approved", "In Production", "Completed"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label className="fform-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={form.sampleApproved} onChange={(e) => handleChange("sampleApproved", e.target.checked)} />
                    <span>Sample Approved</span>
                  </label>
                </div>

                <div className="fform-actions">
                  <button className="fform-save-btn" disabled={saving} onClick={handleSave}>
                    {saving ? "Saving…" : isNew ? "Add Design" : "Save Changes"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2500} />
    </>
  );
};

export default PrintDesignForm;
